import { useEffect, useState } from "react";
import { Library, Loader2, Pencil, Plus, ScrollText, Trash2 } from "lucide-react";
import { ActionOverflowMenu, ActionOverflowMenuItem } from "@doculegis/shared";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { pageActionButtonClassName, pageActionLabelClassName } from "@/lib/page-action-button";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { useAuth } from "@/contexts/AuthContext";
import {
  createLibrary,
  deleteLibrary,
  deleteWorkspace,
  getWorkspace,
  listLibraries,
  listWorkspaces,
  updateLibrary,
  updateWorkspace,
} from "../api/rag-api.js";
import { mapLibrary, mapWorkspace } from "../api/mappers.js";
import { LibraryFolderTile } from "../components/LibraryFolderTile.js";
import { RagPageHeader } from "../components/RagPageHeader.js";
import { CreateLibraryDialog } from "../components/CreateLibraryDialog.js";
import { EditLibraryDialog } from "../components/EditLibraryDialog.js";
import { EditWorkspaceDialog } from "../components/EditWorkspaceDialog.js";
import { LibraryGuidelinesDialog } from "../components/LibraryGuidelinesDialog.js";
import { WorkspaceGuidelinesDialog } from "../components/WorkspaceGuidelinesDialog.js";
import { canManageWorkspaceSettings } from "../lib/workspace-permissions.js";
import {
  canCreateKmLibrary,
  canDeleteKmLibrary,
  canDeleteKmWorkspace,
  canUpdateKmLibrary,
  canUpdateKmWorkspace,
} from "../lib/km-org-permissions.js";
import { useRefreshRagNav } from "../hooks/RagNavRefreshContext.js";
import {
  getLastKmWorkspaceId,
  pickDefaultWorkspaceId,
  setLastKmWorkspaceId,
  workspaceHomePath,
} from "../rag-nav.js";
import type { RagLibrary, RagWorkspace } from "../types.js";

export function WorkspaceDetailPage() {
  const { user, hasPermission, orgRole } = useAuth();
  const navigate = useNavigate();
  const refreshRagNav = useRefreshRagNav();
  const { workspaceId = "" } = useParams();
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [guidelinesOpen, setGuidelinesOpen] = useState(false);
  const [deleteWorkspaceOpen, setDeleteWorkspaceOpen] = useState(false);
  const [deletingWorkspace, setDeletingWorkspace] = useState(false);
  const [editLibrary, setEditLibrary] = useState<RagLibrary | null>(null);
  const [guidelinesLibrary, setGuidelinesLibrary] = useState<RagLibrary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RagLibrary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [workspace, setWorkspace] = useState<RagWorkspace | null>(null);
  const [libraries, setLibraries] = useState<RagLibrary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [{ workspace: workspaceData }, { libraries: libraryData }] = await Promise.all([
        getWorkspace(workspaceId),
        listLibraries(workspaceId),
      ]);
      setWorkspace(mapWorkspace(workspaceData));
      setLibraries(libraryData.map(mapLibrary));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load workspace");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [workspaceId]);

  async function handleDeleteWorkspace() {
    if (!workspace) return;

    setDeletingWorkspace(true);
    try {
      await deleteWorkspace(workspace.id);
      setDeleteWorkspaceOpen(false);
      refreshRagNav();

      const { workspaces: remainingData } = await listWorkspaces();
      const remaining = remainingData.map(mapWorkspace);
      if (getLastKmWorkspaceId() === workspace.id) {
        setLastKmWorkspaceId(null);
      }
      const nextId = pickDefaultWorkspaceId(
        remaining.map((item) => ({
          id: item.id,
          name: item.name,
          description: item.description,
          kind: item.kind,
          ownerId: item.ownerId,
          libraryCount: item.libraryCount,
          memberCount: item.memberCount,
        })),
        user?.id,
        getLastKmWorkspaceId(),
      );
      if (nextId) {
        setLastKmWorkspaceId(nextId);
      }

      navigate(nextId ? workspaceHomePath(nextId) : "/rag", { replace: true });
    } finally {
      setDeletingWorkspace(false);
    }
  }

  async function handleDeleteLibrary() {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      await deleteLibrary(deleteTarget.id);
      setDeleteTarget(null);
      await load();
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <section className="flex flex-1 items-center justify-center p-6">
        <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
      </section>
    );
  }

  if (error || !workspace) {
    return (
      <section className="flex flex-1 flex-col items-center justify-center gap-3 p-6">
        {error ? <p className="text-sm text-red-400">{error}</p> : null}
        <p className="text-sm text-slate-400">
          Workspace not found or the KM service is unavailable. Check that mastra-rag-api is running.
        </p>
      </section>
    );
  }

  const canManageSettings = canManageWorkspaceSettings(user?.id, workspace);
  const canUpdateWorkspace = canUpdateKmWorkspace(hasPermission, user?.id, workspace);
  const canCreateLibrary = canCreateKmLibrary(hasPermission, user?.id, workspace);
  const canUpdateLibrary = canUpdateKmLibrary(hasPermission, user?.id, workspace);
  const canDeleteLibrary = canDeleteKmLibrary(hasPermission, user?.id, workspace);
  const canDeleteWorkspace = canDeleteKmWorkspace(hasPermission, user?.id, workspace, orgRole);

  const mobileActions = [];
  if (canUpdateWorkspace) {
    mobileActions.push({
      id: "edit-workspace",
      icon: Pencil,
      label: "Edit Workspace",
      onClick: () => setEditOpen(true),
    });
  }
  if (canManageSettings) {
    mobileActions.push({
      id: "workspace-guidelines",
      icon: ScrollText,
      label: "Workspace guidelines",
      onClick: () => setGuidelinesOpen(true),
    });
  }
  if (canCreateLibrary) {
    mobileActions.push({
      id: "new-library",
      icon: Plus,
      label: "New Library",
      onClick: () => setCreateOpen(true),
    });
  }
  if (canDeleteWorkspace) {
    mobileActions.push({
      id: "delete-workspace",
      icon: Trash2,
      label: "Delete workspace",
      onClick: () => setDeleteWorkspaceOpen(true),
    });
  }

  return (
    <section className="flex flex-1 flex-col p-6 pb-24 sm:pb-6">
      <RagPageHeader
        icon={Library}
        title={workspace.name}
        subtitle={`${libraries.length} ${libraries.length === 1 ? "library" : "libraries"}`}
        mobileActions={mobileActions}
        action={
          canUpdateWorkspace || canManageSettings || canCreateLibrary || canDeleteWorkspace ? (
            <>
              {canCreateLibrary ? (
                <Button
                  variant="accent"
                  onClick={() => setCreateOpen(true)}
                  title="New Library"
                  aria-label="New Library"
                  className={pageActionButtonClassName}
                >
                  <Plus className="h-4 w-4" />
                  <span className={pageActionLabelClassName}>New Library</span>
                </Button>
              ) : null}
              {canUpdateWorkspace || canManageSettings || canDeleteWorkspace ? (
                <ActionOverflowMenu>
                  {canUpdateWorkspace ? (
                    <ActionOverflowMenuItem
                      icon={Pencil}
                      label="Edit Workspace"
                      onClick={() => setEditOpen(true)}
                    />
                  ) : null}
                  {canManageSettings ? (
                    <ActionOverflowMenuItem
                      icon={ScrollText}
                      label="Workspace guidelines"
                      onClick={() => setGuidelinesOpen(true)}
                    />
                  ) : null}
                  {canDeleteWorkspace ? (
                    <ActionOverflowMenuItem
                      icon={Trash2}
                      label="Delete workspace"
                      variant="destructive"
                      onClick={() => setDeleteWorkspaceOpen(true)}
                    />
                  ) : null}
                </ActionOverflowMenu>
              ) : null}
            </>
          ) : null
        }
      />
      <div className="mb-8 flex flex-wrap gap-x-6 gap-y-8">
        {libraries.map((library) => (
          <LibraryFolderTile
            key={library.id}
            to={`/rag/workspaces/${workspaceId}/libraries/${library.id}/documents`}
            title={library.name}
            actions={
              canUpdateLibrary || canDeleteLibrary ? (
                <ActionOverflowMenu
                  size="xs"
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                >
                  {canUpdateLibrary ? (
                    <ActionOverflowMenuItem
                      icon={ScrollText}
                      label="Library guidelines"
                      onClick={() => setGuidelinesLibrary(library)}
                    />
                  ) : null}
                  {canUpdateLibrary ? (
                    <ActionOverflowMenuItem
                      icon={Pencil}
                      label="Edit library"
                      onClick={() => setEditLibrary(library)}
                    />
                  ) : null}
                  {canDeleteLibrary ? (
                    <ActionOverflowMenuItem
                      icon={Trash2}
                      label="Delete library"
                      variant="destructive"
                      onClick={() => setDeleteTarget(library)}
                    />
                  ) : null}
                </ActionOverflowMenu>
              ) : undefined
            }
          />
        ))}
      </div>

      <CreateLibraryDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreate={async (body) => {
          await createLibrary(workspaceId, body);
          await load();
        }}
      />
      <EditLibraryDialog
        open={editLibrary != null}
        onOpenChange={(open) => {
          if (!open) setEditLibrary(null);
        }}
        library={editLibrary}
        onSave={async (libraryId, body) => {
          await updateLibrary(libraryId, body);
          await load();
        }}
      />
      <ConfirmDialog
        isOpen={deleteWorkspaceOpen}
        onClose={() => setDeleteWorkspaceOpen(false)}
        onConfirm={handleDeleteWorkspace}
        title={workspace.isDefault ? "Delete default workspace" : "Delete workspace"}
        description={
          workspace.isDefault
            ? `Delete default workspace "${workspace.name}"? All libraries, documents, and chats in this workspace will be permanently removed. A new default workspace will be created automatically the next time you sign in.`
            : `Delete workspace "${workspace.name}"? All libraries, documents, and chats in this workspace will be permanently removed.`
        }
        confirmLabel="Delete workspace"
        variant="destructive"
        isLoading={deletingWorkspace}
      />
      <ConfirmDialog
        isOpen={deleteTarget != null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteLibrary}
        title="Delete library"
        description={
          deleteTarget
            ? `Delete library "${deleteTarget.name}"? All documents and chats in this library will be permanently removed.`
            : ""
        }
        confirmLabel="Delete library"
        variant="destructive"
        isLoading={deleting}
      />
      <EditWorkspaceDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        workspace={{
          id: workspace.id,
          name: workspace.rawName,
          description: workspace.description,
        }}
        onSave={async (id, body) => {
          await updateWorkspace(id, body);
          refreshRagNav();
          await load();
        }}
      />
      <WorkspaceGuidelinesDialog
        open={guidelinesOpen}
        onOpenChange={setGuidelinesOpen}
        workspace={{ id: workspace.id, name: workspace.name }}
      />
      <LibraryGuidelinesDialog
        open={guidelinesLibrary != null}
        onOpenChange={(open) => {
          if (!open) setGuidelinesLibrary(null);
        }}
        library={
          guidelinesLibrary
            ? { id: guidelinesLibrary.id, name: guidelinesLibrary.name }
            : null
        }
      />
    </section>
  );
}
